import { JSX, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Tabs from "../../components/Tabs";
import SubButton from "../../components/SubButtons";
import ClassHeaderSection from "./ClassHeaderSection";
import Students from "./Students";
import TeachersTab from "./TeachersTab";
import SubjectsTab from "./SubjectsTab";
import AddSubjectToClass from "../../components/modal/AddSubjectToClass";
import { dropDownWhite, trashOutline } from "../../asset";
import useUrlTab from "../../util/hooks/useUrlTab";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import { useBranchCode } from "../../util/hooks/useBranch";
import classesApis from "../../api/classesApis";
import GroupApis from "../../api/groupApis"
import { AppDispatch, RootState } from "../../store";
import { getStudents } from "../../features/student/StudentSlice";
import { Group } from "../../types/groupAndHouses";
import useDropdown from "../../util/hooks/useDropDown";
import AssignTeachersToClass from "../../components/modal/AssignTeacherToClass";

const tabOptions = ["Students", "Teachers", "Subjects"];

export default function ClassesDetails() {
  const { classId } = useParams();
  const numericClassId = Number(classId);

  const dispatch = useDispatch<AppDispatch>();
  const branchCode = useBranchCode();
  const { confirm, modal } = useConfirmToast();
  const { activeTab, handleTabChange } = useUrlTab(tabOptions);
  const [teacherRefreshKey, setTeacherRefreshKey] = useState(0);

  const { accessibleClassRooms: classes } = useSelector(
    (state: RootState) => state.access,
  );

  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [selectedTeachers, setSelectedTeachers] = useState<number[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<number[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [showAssignSubjectModal, setShowAssignSubjectModal] = useState(false);
  const [prefilledSubjectId, setPrefilledSubjectId] = useState<number | null>(
    null,
  );
  const [subjectRefreshKey, setSubjectRefreshKey] = useState(0);
  const [showAssignTeacherModal, setShowAssignTeacherModal] = useState(false);

  const groupDropdown = useDropdown();
  const classDropdown = useDropdown();

  useEffect(() => {
    const loadGroups = async () => {
      try {
        const res = await GroupApis.getGroups({
          page: 0,
          size: 100,
          branchCode,
        });
        setGroups(res.data.content);
      } catch {
        console.error("Failed to load groups");
      }
    };

    if (branchCode) loadGroups();
  }, [branchCode]);

  //Student Actions

  const handleMoveStudents = async (
    targetClassId: number,
    className: string,
  ) => {
    classDropdown.closeDropdown();

    const confirmed = await confirm(
      `Move ${selectedStudents.length} student(s) to ${className}?`,
      { confirmText: "Yes, Move" },
    );
    if (!confirmed) return;

    await Promise.all(
      selectedStudents.map((id) =>
        classesApis.moveStudentToClass(targetClassId, id),
      ),
    );

    setSelectedStudents([]);

    dispatch(
      getStudents({
        page: 0,
        size: 100,
        branchCode,
        classId,
      }),
    );
  };

  const handleRemoveStudent = async () => {
    const confirmed = await confirm(
      "Are you sure you want to remove selected student from class?",
      { confirmText: "Yes, Remove" },
    );

    if (!confirmed) return;

    try {
      await Promise.all(
        selectedStudents.map((id) => classesApis.removeStudentFromClass(id)),
      );

      setSelectedStudents([]);

      dispatch(
        getStudents({
          page: 0,
          size: 100,
          branchCode,
          classId,
        }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  // Teacher Actions

  const handleRemoveTeachers = async () => {
    const confirmed = await confirm(
      "Remove selected teacher(s) from this class?",
      { confirmText: "Yes, Remove" },
    );
    if (!confirmed) return;

    await Promise.all(
      selectedTeachers.map((id) =>
        classesApis.unAssignTeacher(numericClassId, id),
      ),
    );

    setSelectedTeachers([]);
    setTeacherRefreshKey((prev) => prev + 1);
  };

  const handleMakeClassTeacher = async () => {
    if (selectedTeachers.length !== 1) return;

    const confirmed = await confirm("Assign as class teacher?", {
      confirmText: "Yes, Assign",
    });
    if (!confirmed) return;

    await classesApis.makeClassTeacher(numericClassId, selectedTeachers[0]);

    setSelectedTeachers([]);
    setTeacherRefreshKey((prev) => prev + 1);
  };

  // sybjects
  const handleDeleteSubjects = async () => {
    const confirmed = await confirm(
      "Remove selected subjects from this class?",
    );
    if (!confirmed) return;

    await Promise.all(
      selectedSubjects.map((id) => classesApis.deleteAssignSubjects(id)),
    );

    setSelectedSubjects([]);
    setSubjectRefreshKey((prev) => prev + 1);
  };

  // add student to group
  const handleAddStudentsToGroup = async (groupId: number) => {
    groupDropdown.closeDropdown();

    const confirmed = await confirm(
      `Are you sure you want to add this student to group?`,
      { confirmText: "Yes, Add" },
    );

    if (!confirmed) return;

    try {
      await Promise.all(
        selectedStudents.map((admissionNumber) =>
          GroupApis.addStudentToGroup(groupId, admissionNumber),
        ),
      );

      setSelectedStudents([]);
    } catch (error) {
      console.error(error);
    }
  };

  const openAssignTeacherModal = () => {
    setShowAssignTeacherModal(true);
  };

  const tabContent: Record<string, JSX.Element> = useMemo(
    () => ({
      Students: (
        <Students
          selectedStudents={selectedStudents}
          onSelect={setSelectedStudents}
          currentClassId={numericClassId}
        />
      ),
      Teachers: (
        <TeachersTab
          selectedTeachers={selectedTeachers}
          onSelect={setSelectedTeachers}
          refreshKey={teacherRefreshKey}
        />
      ),
      Subjects: (
        <SubjectsTab
          selectedSubjects={selectedSubjects}
          onSelectSubjects={setSelectedSubjects}
          refreshKey={subjectRefreshKey}
          onAssignNewStaff={(subjectId) => {
            setPrefilledSubjectId(subjectId);
            setShowAssignSubjectModal(true);
          }}
        />
      ),
    }),
    [
      selectedStudents,
      selectedTeachers,
      selectedSubjects,
      subjectRefreshKey,
      numericClassId,
      teacherRefreshKey,
    ],
  );

  return (
    <div className="flex flex-col gap-5">
      <ClassHeaderSection openAssignTeacherModal={openAssignTeacherModal} />

      <div className="flex justify-between items-center">
        <Tabs
          tabs={tabOptions}
          onTabChange={handleTabChange}
          activeTab={activeTab}
        />

        {/* ---------- STUDENTS ---------- */}
        {activeTab === "Students" && (
          <div className="flex gap-3">
            <SubButton
              text="Delete"
              color="bg-error-600"
              disabled={selectedStudents.length === 0}
              icon={trashOutline}
              onClick={handleRemoveStudent}
            />
            {/* Group Dropdown */}
            <div className="relative" ref={groupDropdown.dropdownRef}>
              <SubButton
                text="Add to Groups"
                color="bg-warning-700"
                icon={dropDownWhite}
                iconPosition="right"
                disabled={!selectedStudents.length}
                onClick={groupDropdown.toggleDropdown}
              />
              {groupDropdown.isOpen && (
                <div className="absolute right-0 top-10 max-h-80 overflow-y-auto w-48 bg-white border rounded-md shadow-lg z-50">
                  {groups.map((group) => (
                    <button
                      key={group.groupId}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100"
                      onClick={() => handleAddStudentsToGroup(group.groupId)}
                    >
                      {group.groupName}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Move Class Dropdown */}
            <div className="relative" ref={classDropdown.dropdownRef}>
              <SubButton
                color="bg-success-700"
                text="Move to Class"
                icon={dropDownWhite}
                iconPosition="right"
                disabled={!selectedStudents.length}
                onClick={classDropdown.toggleDropdown}
              />
              {classDropdown.isOpen && (
                <div className="absolute right-0 top-10 w-48 max-h-80 overflow-y-auto bg-white border rounded-md shadow-lg z-50">
                  {classes.map((cls) => (
                    <button
                      key={cls.classId}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100"
                      onClick={() =>
                        handleMoveStudents(cls.classId, cls.className)
                      }
                    >
                      {cls.className}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TEACHERS */}
        {activeTab === "Teachers" && (
          <div className="flex gap-3">
            <SubButton
              text="Delete"
              icon={trashOutline}
              color="bg-error-600"
              disabled={!selectedTeachers.length}
              onClick={handleRemoveTeachers}
            />
            <SubButton
              text="Assign Class Teacher"
              color="bg-success-700"
              disabled={selectedTeachers.length !== 1}
              onClick={handleMakeClassTeacher}
            />
          </div>
        )}

        {/* SUBJECTS */}
        {activeTab === "Subjects" && (
          <div className="flex gap-3">
            <SubButton
              text="Delete"
              icon={trashOutline}
              color="bg-error-600"
              disabled={!selectedSubjects.length}
              onClick={handleDeleteSubjects}
            />
            <SubButton
              text="Add Subject"
              color="bg-success-700"
              onClick={() => {
                setPrefilledSubjectId(null);
                setShowAssignSubjectModal(true);
              }}
            />
          </div>
        )}
      </div>

      {tabContent[activeTab]}

      {showAssignSubjectModal && (
        <AddSubjectToClass
          subjectId={prefilledSubjectId}
          onClose={() => setShowAssignSubjectModal(false)}
          onSuccess={() => {
            setShowAssignSubjectModal(false);
            setSubjectRefreshKey((prev) => prev + 1);
          }}
        />
      )}

      {modal}

      {showAssignTeacherModal && (
        <AssignTeachersToClass
          onClose={() => setShowAssignTeacherModal(false)}
          onSuccess={() => {
            setTeacherRefreshKey((prev) => prev + 1); // 🔥 SAME KEY
            setShowAssignTeacherModal(false);
          }}
        />
      )}
    </div>
  );
}
